import Warning20 from '@carbon/icons-react/es/warning/20';
import { useControllableValue } from 'ahooks';
import React, { FC, useState } from 'react';
import { confirm, Modal } from 'components/atoms/modal';
import { Option } from 'components/molecules/squad-picker/types';
import { withHideIfNotAllowed } from 'components/organisms/permission-controller';
import { PermissionRuleName } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { NewTaskForm, StartAndDueDate } from './form';

interface NewTaskModalProps {
  isVisible?: boolean;
  onCancel?: () => void;
  trigger: (
    toggleVisibility: () => void,
    isVisible?: boolean,
  ) => React.ReactNode;
}
const NewTaskModalWithoutPermissionCheck: FC<NewTaskModalProps> = (props) => {
  const { trigger, onCancel } = props;
  const [isVisible, setIsVisible] = useControllableValue<boolean>(props, {
    valuePropName: 'isVisible',
    defaultValue: false,
  });

  const toggleVisibility = () => {
    if (isVisible) {
      onCancel?.();
    }
    setIsVisible(!isVisible);
  };

  const [taskDescription, setTaskDescription] = useState<string>('');
  const [taskName, setTaskName] = useState<string>('');
  const [assignee, setAssignee] = useState<Option[]>([]);
  const [startAndDueDate, setStartAndDueDate] = useState<StartAndDueDate>([
    null,
    null,
  ]);

  const hasFormData = (): boolean => {
    return (
      taskName.length > 0 ||
      taskDescription.length > 0 ||
      Boolean(startAndDueDate[0]) ||
      Boolean(startAndDueDate[1]) ||
      Boolean(assignee?.length)
    );
  };

  const clearForm = () => {
    setTaskName('');
    setStartAndDueDate([null, null]);
    setAssignee([]);
    setTaskDescription('');
  };

  const closeModal = () => {
    clearForm();
    toggleVisibility();
  };

  const confirmTitle = useTranslation('new_task.cancel.confirm.title');
  const confirmContent = useTranslation('new_task.cancel.confirm.content');
  const showConfirm = () => {
    confirm({
      title: confirmTitle,
      icon: <Warning20 />,
      content: confirmContent,
      onOk() {
        closeModal();
      },
      onCancel() {},
    });
  };

  const handleCloseModal = (e: React.MouseEvent<HTMLElement>) => {
    const {
      currentTarget: { className },
    } = e;

    if (className && className.includes('ant-modal-wrap')) {
      toggleVisibility();
    } else if (hasFormData()) {
      showConfirm();
    } else {
      closeModal();
    }
  };

  return (
    <>
      {trigger(toggleVisibility, isVisible)}
      {isVisible && (
        <Modal
          visible={isVisible}
          confirmLoading={false}
          onCancel={handleCloseModal}
          footer={null}
          title=""
          maskClosable={false}
          keyboard={false}
        >
          <NewTaskForm
            taskName={taskName}
            setTaskName={setTaskName}
            taskDescription={taskDescription}
            setTaskDescription={setTaskDescription}
            assignee={assignee}
            setAssignee={setAssignee}
            startAndDueDate={startAndDueDate}
            setStartAndDueDate={setStartAndDueDate}
            onSuccess={closeModal}
          />
        </Modal>
      )}
    </>
  );
};

const NewTaskModalWithPermissionCheck = withHideIfNotAllowed(
  NewTaskModalWithoutPermissionCheck,
);

export const NewTaskModal: FC<NewTaskModalProps> = (props) => {
  return (
    <NewTaskModalWithPermissionCheck
      {...props}
      ruleName={PermissionRuleName.TaskCreateAction}
    />
  );
};
